import { Alert, Button, Col, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Nav, NavItem, NavLink, Row, Table } from "reactstrap";
import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs";
import { DASHBOARD_ROOT } from "../../components/Routes/routingConstants";
import { useEffect, useRef, useState } from "react";
import { Formik, Field, FormikProps } from "formik";
import FormError from "../../components/Shared/Forms/FormError";
import TextFormInput from "../../components/Shared/Forms/TextFormInput";
import AddUserValidator from "../../validators/AddUserValidator";
import { DateFormat, UserLicensesType, UserTypeOptions } from "../../helper/constants";
import { Organisation } from '../../interfaces/models/Organisation';
import { useOrganisation } from '../../hooks/useOrganisation';
import  { UserPaginated, UserDocs } from "../../interfaces/hookModels/UserPaginated";
import { useUserPaginated } from "../../hooks/useUserDetails";
import Loading from "../../components/Loading";
import PaginationSet from "../../components/Shared/Forms/PaginationSet";
import moment from "moment";
import { HandleReformatDate } from '../../helper/Utilities';
import DateTime from "react-datetime";
import { addUserAndLicense } from "../../graphql/content-management/addUserAndLicense"
import { useMutation } from "@apollo/client";
import toast from "react-hot-toast";
import UserMasterListFilterParams from "../../interfaces/filters/UserMasterListFilterParams"

interface ModalProperties { 
    action : string;
    buttonName : string;
}

interface IUser {
    _id : string;
    firstName : string;
    lastName : string;
    email : string;
    password : string;
    organisation : string;
    licenseList : string[];
    blocked : boolean;
    dateAllocated : string;
    expiryDate : string;
    azId : string;
    userType: string;
}

const UserMasterListPage = () => {

//#region  Initialization 
    
    const breadcrumbsData = [
        { name: 'Content List', path: `${DASHBOARD_ROOT}/content-management/` },
        { name: 'User Master List', path: `${DASHBOARD_ROOT}/management/users` },
    ];

    const [organisations, setOrganisations] = useState<Organisation[]>([]);
    const [users, setUsers] = useState<UserPaginated>({ total_pages : 0, page : 0, docs : [] });

    const [filterParams, setFilterParams] = useState<UserMasterListFilterParams>({
        search: '',
        organisation: '',
        page : 1,
        limit : 25
    });

    const [filterInputParams, setFilterInputParams] = useState<UserMasterListFilterParams>({
        search: '',
        organisation: '',
        page : 1,
        limit : 25
    });


    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isOnProcess, setIsOnProcess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [modalProperty, setModalProperty] = useState<ModalProperties>({ action : "", buttonName : "" });

    const [selectedUser, setSelectedUser] = useState<Partial<IUser> | null>(null);
    const formikRef = useRef<FormikProps<IUser>>(null);

    let timer = useRef<number | null> (null);
    
    const { data : organisationData, loading : organisationLoading} = useOrganisation();
    const { data , loading , error, refetch} = useUserPaginated(filterParams);

    const [addUserAndLicenseHook] = useMutation(addUserAndLicense,{
        onCompleted : () =>{
            toast.success("User was successfully " + modalProperty.action + "ed");
        },
        onError : () =>{
            toast.error("Something went wrong whilst Adding the User . Please try again or if issue persists, please contact us");
        }
    });

    const GeneratePassword = (length = 10) => {
        const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#&_+?";
        let password = "";
        for (let i = 0; i < length; i++) {
            password += characters.charAt(Math.floor(Math.random() * characters.length));
        }
        return password;
    }

    const filter = (page : number, limit : number, searchText : string, searchOrganisation : string) => {

        searchText = searchText?.trim().toLowerCase();

        setIsLoading(true);

        if(timer?.current) {
            clearTimeout(timer.current);
            timer.current = null;
        }

        timer.current = window.setTimeout(async () => {
            await refetch({ page : page, limit : limit, organisation : searchOrganisation, search : searchText });
            setIsLoading(false);
        },1500);
        
    }

    const OnPageChange = async (page : number) => {
        if(isLoading || page === filterInputParams.page) { return; }
        setFilterInputParams((prev) => ({...prev, page : page}));
        filter(page,filterInputParams.limit, filterInputParams.search, filterInputParams.organisation);

    }

    const HandleSearchChange = (searchText : string) => {
        
        setFilterInputParams((prev) => ({...prev, search : searchText}));
        filter(1,filterInputParams.limit, searchText, filterInputParams.organisation);
    }

    const HandleOrganisationChange = (orgSearch : string) => {
        
        setFilterInputParams((prev) => ({...prev, organisation : orgSearch}));
        filter(1,filterInputParams.limit, filterInputParams.search, orgSearch);
    }

    const HandleClearFilters = () => {
        setFilterInputParams((prev) => ({...prev, organisation : "", search : ""}));
        filter(filterInputParams.page, filterInputParams.limit, "", "");
    }

    useEffect(() =>{
        if(organisationData) {
            setOrganisations(organisationData.organisations);
        }
    },[organisationData]);

    useEffect(() => {
        if(data) {
            setUsers(data.users_paginated);
        }
    },[data]);


//#endregion Initialization
//#region Adding User
    const ToggleModal = () => {
        setIsModalOpen(!isModalOpen);
    }

    const OpenAddModal = async () => {

        const password = GeneratePassword();
        setSelectedUser({ 
            _id : "",
            firstName : "", 
            lastName : "", 
            email : "", 
            password : password, 
            organisation : "", 
            licenseList : ["desk.coach"],
            blocked : false,
            dateAllocated : moment().format("YYYY-MM-DD"),
            expiryDate : moment().add(5,'year').format("YYYY-MM-DD"),
            azId : "",
            userType: "member"
        });
        ToggleModal();
        setModalProperty({ action : "add", buttonName : "Add" });
        
    }

    const OpenEditModal = (item : UserDocs) => {
        const randomPassword = GeneratePassword();
        setSelectedUser({ 
            _id : item._id,
            firstName : item.first_name ? item.first_name : item.name, 
            lastName : item.last_name ? item.last_name :"", 
            email : item.email, 
            password : randomPassword, 
            organisation : item.organisation_id?._id, 
            licenseList : item.licenses?.split(","),
            blocked : item.blocked,
            dateAllocated : moment(item.date_allocated).format("YYYY-MM-DD"),
            expiryDate : moment(item.expiry_date).format("YYYY-MM-DD"),
            azId : item.az_id,
            userType: item.user_type
        });
        ToggleModal();
        setModalProperty({ action : "edit", buttonName : "Update" });
    }

    const TriggerSubmit = ()=>{
        formikRef.current.submitForm();
    }

    const HandleSubmit = async (formData : IUser) => {
        if(isOnProcess) { return; }
        setIsOnProcess(true);
        
        formData.firstName = formData.firstName.trim();
        formData.lastName = formData.lastName.trim();
        formData.email = formData.email.trim();
        // let response = null;
        // const fullName = formData.firstName + " " + formData.lastName;
        // const mailNickName = formData.email.split("@")[0];

        // if(modalProperty.action === "add") {
        //     response = await axios.post("https://graph.microsoft.com/v1.0/users",
        //         {
        //             "accountEnabled": true,
        //             "displayName": fullName,
        //             "givenName": formData.firstName,
        //             "surname": formData.lastName,
        //             "userPrincipalName": mailNickName + "@"+  domain,
        //             "mailNickname" : mailNickName,
        //             "passwordProfile": {
        //                 "forceChangePasswordNextSignIn": false,
        //                 "password": formData.password
        //             },
        //             "identities": [
        //             {
        //               "signInType": "emailAddress",
        //               "issuer": domain,
        //               "issuerAssignedId": formData.email
        //             }
        //           ]
        //         },
        //         {
        //             headers: {
        //                 Authorization: `Bearer ${token}`,
        //                 "Content-Type": "application/json"
        //             }
        //         }
        //     ).then((res)=>  res).catch((err) => err);
    
        // }
        // else if(modalProperty.action === "edit") {
        //     response = await axios.patch(`https://graph.microsoft.com/v1.0/users/{${selectedUser.azId}}`,
        //         {
        //             "accountEnabled": formData.blocked === false,
        //             "displayName": fullName,
        //             "givenName": formData.firstName,
        //             "surname": formData.lastName,
        //             "mailNickname" : mailNickName,
        //             "identities": [
        //             {
        //               "signInType": "emailAddress",
        //               "issuer": domain,
        //               "issuerAssignedId": formData.email
        //             }
        //           ]
        //         },
        //         {
        //             headers: {
        //                 Authorization: `Bearer ${token}`,
        //                 "Content-Type": "application/json"
        //             }
        //         }
        //     ).then((res)=>  res).catch((err) => err);
    
        // }

        // if(response.status !== 204) {
        //     toast.error("Sorry something went wrong on adding users on Azure");
        //     setIsOnProcess(false);
        //     return;
        // }

        
        // formData.azId = response.data.id;

        const isAdded : boolean = await addUserAndLicenseHook({
            variables : {
                input : formData
            }
        }).then((res) => res.errors ? false : true).catch(()=>false);
        setIsOnProcess(false);
        if(!isAdded) {
            return;
        }
        ToggleModal();
        setIsLoading(true);
        await refetch({ page : filterInputParams.page, limit : filterInputParams.limit, organisation : filterInputParams.organisation, search : filterInputParams.search });
        setIsLoading(false);
    }
//#endregion Adding User
    return (
        <>
            <Breadcrumbs 
                crumbs={breadcrumbsData}
            />
            {
                error && 
                <Alert color="danger"> Error on getting Users</Alert>
            }


            <Row className="pb-3">
                <Col>
                    <Nav tabs>
                        <NavItem>
                            <NavLink className={filterParams.onboarded !== false ? 'active': undefined} onClick={() => setFilterParams(prev => ({...prev, onboarded: undefined }))}>
                                <span className="text-sm font-weight-600">All Users</span>
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink className={filterParams.onboarded === false ? 'active': undefined} onClick={() => setFilterParams(prev => ({...prev, onboarded: false }))}>
                                <span className="text-sm font-weight-500">In-Review</span>
                            </NavLink>
                        </NavItem>
                    </Nav>
                </Col>
            </Row>


            <Row className="pb-3">
                <Col className="d-flex">
                    <label className="mt-2 mr-2">Search: </label>
                    <Input className="w-100" type="text" value={filterInputParams.search} onChange={(e) => HandleSearchChange(e.currentTarget.value)} placeholder="Search name here" 
                    />
                </Col>
                <Col className="d-flex">
                    <label className="mt-2 mr-2">Organisation: </label>
                    <Input type="select" 
                        value={filterInputParams.organisation} onChange={(e) => { HandleOrganisationChange(e.currentTarget.value); }} 
                        disabled={organisationLoading}
                    >
                        <option value="">All</option>
                        {
                            organisations.map((item, index) => (
                                <option key={index} value={item._id}>{item.name}</option>
                            ))
                        }
                    </Input>
                    <Button color="secondary ml-3" onClick={HandleClearFilters} >Clear</Button>
                    <Button color="primary" style={{width:300}} onClick={OpenAddModal}>Add User</Button>
                </Col>
            </Row>

            <div className="table-responsive bg-white"  style={{minHeight:650}}>
                <Table bordered hover className="mb-0">
                    <thead>
                        <tr>
                            <td>Name</td>
                            <td>Email</td>
                            <td>Organisation</td>
                            <td>Status</td>
                            <td>Subscription</td>
                            <td>Actions</td>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            users.docs.length === 0 && !loading && !isLoading &&
                            <tr>
                                <td colSpan={6} className="text-center">No Records Found</td>
                            </tr>
                        }
                        {
                            (loading || isLoading) &&
                            <tr>
                                <td colSpan={6}><Loading /></td>
                            </tr>
                        }

                        {
                            !loading && !isLoading && users.docs.map((item, index) => 
                            
                                <tr key={index}>
                                    <td>{item.name}</td>
                                    <td>{item.email}</td>
                                    <td>{item.organisation_id?.name}</td>
                                    <td>
                                        {
                                            item.blocked ? 
                                            <span className="badge badge-info">Blocked</span>
                                            :
                                            item.az_id ? <span className="badge badge-success">Active</span> :
                                            <span className="badge badge-light">In-Review</span>
                                        }
                                    </td>
                                    <td></td>
                                    <td>
                                        <Button color="primary" size="sm" className="mr-2" onClick={() => OpenEditModal(item)}>{item.az_id ? 'Edit' : 'Add'}</Button>
                                    </td>
                                </tr>
                            )
                        }
                    </tbody>
                </Table>
            </div>
            <div className="d-flex justify-content-end pt-3">
                <PaginationSet 
                    totalPage={users.total_pages}
                    page={users.page}
                    onPageChange={OnPageChange}
                />
            </div>           
            <Modal
                id="user-manage-modal"
                className="modal-xl"
                backdrop={true}
                isOpen={isModalOpen}>
                <ModalHeader toggle={ToggleModal} className="pb-0">
                    <p className="text-lg">{modalProperty.buttonName} User</p>
                </ModalHeader>
                <ModalBody className="pt-0">
                    {
                        <Formik
                            innerRef={formikRef}
                            initialValues={selectedUser}
                            validationSchema={AddUserValidator}
                            onSubmit={async (value : IUser) =>{
                                await HandleSubmit(value);
                            }}
                        >
                            {({touched, errors, setFieldValue, values}) => (
                                <Form>
                                    <Row>
                                        <Col md={12}>
                                            <TextFormInput
                                                name={"firstName"}
                                                placeholder="Enter First Name"
                                                label="First Name"
                                                required={true}
                                            />
                                            <TextFormInput
                                                name={"lastName"}
                                                placeholder="Enter Last Name"
                                                label="Last Name"
                                                required={true}
                                            />
                                            <TextFormInput
                                                name={"email"}
                                                placeholder="Enter email"
                                                label="Email"
                                                required={true}
                                            />
                                            {!selectedUser.azId && (
                                                <TextFormInput
                                                    name={"password"}
                                                    placeholder="Enter initial password"
                                                    label="Password"
                                                    required={true}
                                                />
                                            )}
                                            <span  className={modalProperty.action !== "add" ? "d-none " : "text-warning small "}>NOTE: Please save this password, as it will not be shown again after user creation.</span>
                                            <FormGroup className="mb-0">
                                                <Label for="organisation">Organisation *</Label>
                                                <Field as="select" name="organisation" className="form-control" required disabled={organisationLoading}>
                                                    <option value="">Select Organisation</option>
                                                    {
                                                        organisations.map((item, index) => (
                                                            <option key={index} value={item._id}>{item.name}</option>
                                                        ))
                                                    }
                                                </Field>
                                                <FormError>{touched.organisation && (errors.organisation)}</FormError>
                                            </FormGroup>
                                            <FormGroup className="mb-0">
                                                <Label for="organisation">User Type *</Label>
                                                <Field as="select" name="userType" className="form-control" required>
                                                    {
                                                        UserTypeOptions.map((item, index) => (
                                                            <option key={index} value={item.value_string}>{item.name}</option>
                                                        ))
                                                    }
                                                </Field>
                                                <FormError>{touched.userType && (errors.userType)}</FormError>
                                            </FormGroup>
                                            
                                        </Col>
                                        <Col md={12}>
                                            <h4>License Information</h4>
                                        </Col>
                                        <Col md={8}>
                                            <FormGroup className={modalProperty.action === "add" ? "mb-0 d-none" :"mb-0"} >
                                                <Label >Blocked *</Label>
                                                <Field as="select" name="blocked" className="form-control" 
                                                onChange={(e) => {
                                                    const value = e.currentTarget.value;
                                                    setFieldValue("blocked",value === "true");
                                                }}>
                                                    <option value="false">No</option>
                                                    <option value="true">Yes</option>
                                                </Field>
                                                <FormError>{touched.blocked && (errors.blocked)}</FormError>
                                            </FormGroup>
                                            <FormGroup className="mb-0">
                                                <Label>Date Allocated *</Label>
                                                <DateTime 
                                                    className="w-100" 
                                                    onChange={(date) => {
                                                        const reformated = HandleReformatDate(date);
                                                        setFieldValue("dateAllocated",reformated)   
                                                    }} 
                                                    dateFormat={DateFormat} 
                                                    timeFormat={false} 
                                                    inputProps={{readOnly : true ,className:"form-control bg-white"}} 
                                                    initialValue={new Date(values.dateAllocated)}
                                                />
                                                <FormError>{touched.dateAllocated && (errors.dateAllocated)}</FormError>
                                            </FormGroup>
                                            <FormGroup>
                                                <Label>Expiry Date *</Label>
                                                <DateTime 
                                                    className="w-100" 
                                                    onChange={(date) => {
                                                        const reformated = HandleReformatDate(date);
                                                        setFieldValue("expiryDate",reformated)   
                                                    }} 
                                                    dateFormat={DateFormat} 
                                                    timeFormat={false} 
                                                    inputProps={{readOnly : true ,className:"form-control bg-white"}} 
                                                    initialValue={new Date(values.expiryDate)}
                                                />
                                                <FormError>{touched.expiryDate && (errors.expiryDate)}</FormError>
                                            </FormGroup>
                                        </Col>
                                        
                                        <Col md={4} >
                                            <div className="table-reponsive">
                                                <Table bordered>
                                                    <colgroup>
                                                        <col width="15%"></col>
                                                        <col width="85%"></col>
                                                    </colgroup>
                                                    <thead>
                                                        <tr>
                                                            <th></th>
                                                            <th>License</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {
                                                            UserLicensesType.map((item, index) => (
                                                                <tr key={index}>
                                                                    <td>
                                                                        <Field className="form-control" type="checkbox" name="licenseList" value={item} disabled={item === "desk.coach"} />
                                                                    </td>
                                                                    <td >
                                                                        {item} 
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        }
                                                    </tbody>
                                                </Table>
                                            </div>
                                            <FormError>{errors.licenseList}</FormError>
                                        </Col>
                                    </Row>

                                </Form>
                            )}
                        </Formik>
                    }
                    
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" disabled={isOnProcess} onClick={ToggleModal} >Close </Button>
                    <Button color="primary" disabled={isOnProcess} onClick={TriggerSubmit} >{modalProperty.buttonName}</Button>
                </ModalFooter>
            </Modal>
        </>
    )
};

export { UserMasterListPage as UserMasterList };
